import React from "react";
import { Box, Fade } from "@mui/material";
import StepUpSpinner from "./StepUpSpinner";

/**
 * Full-page dashboard loader. Reuses the branded [[StepUpSpinner]]
 * (official Step Up Academy logo + circular ring) inside a background
 * that matches the dashboard shell (#f8fafc). Fades out smoothly once
 * initial dashboard data has settled.
 *
 * a11y: role="status" + aria-live="polite" so screen readers announce
 * the load; decorative spinner internals are aria-hidden inside
 * StepUpSpinner. Respects prefers-reduced-motion via each keyframe's
 * user-agent handling.
 */
interface Props {
  visible: boolean;
}

const DashboardLoader: React.FC<Props> = ({ visible }) => (
  <Fade in={visible} timeout={{ enter: 0, exit: 320 }} unmountOnExit>
    <Box
      role="status"
      aria-live="polite"
      aria-label="Loading your dashboard"
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f8fafc",
        zIndex: 1200,
        px: 2,
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" },
        },
      }}
    >
      <StepUpSpinner size="large" label="Loading your dashboard…" />
    </Box>
  </Fade>
);

export default DashboardLoader;
